/**
 * The nearest station that reports, for an aerodrome that does not.
 *
 * One rule stands above the layout: **a neighbour's report may never be readable
 * as this aerodrome's own.** So it is inset behind its own rule, it is headed
 * "nächste Meldung", it names the distance and the direction from the aerodrome
 * that is silent, and where it was written onto the form it says whose report
 * stands in that row. Four sentences saying the same thing, because the one time
 * it is misread is the time it matters.
 */
import { useT } from '../../i18n';
import { CategoryDot } from './CategoryDot';
import { ObsAge } from './ObsAge';
import { TafTimeline } from './TafTimeline';
import { DECODED_ROWS, type NearbyCard as Model } from './model';

export function NearbyCard({
  card,
  now,
  etd,
  eta,
}: {
  card: Model;
  now: number;
  etd?: number | null | undefined;
  eta?: number | null | undefined;
}) {
  const t = useT();
  const rows = card.decoded;
  return (
    <div className="wx-near">
      <p className="ae-eyebrow">{t('wx.nearby.title')}</p>
      <div className="wx-near-head">
        <CategoryDot cat={card.category} size="sm" />
        <span className="wx-near-id">{card.id}</span>
        {card.name ? <span className="wx-near-name">{card.name}</span> : null}
        <ObsAge at={card.obsTime} now={now} />
      </div>
      <p className="wx-near-dist ae-num">
        {t('wx.nearby.dist', { dist: card.dist, dir: t('wx.dir.' + card.dir), of: card.of })}
      </p>
      {rows ? (
        <dl className="wx-values compact">
          {DECODED_ROWS.filter(k => rows[k] !== '').map(k => (
            <div key={k}>
              <dt>{t('wx.row.' + k)}</dt>
              <dd className="ae-num">{rows[k]}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {/* the neighbour's forecast is the only one this leg of the flight has —
          it is shown as the neighbour's, like everything else in this block */}
      <TafTimeline periods={card.periods} etd={etd} eta={eta} />
      {/* the cell that survives onto paper — and the only place the substitution
          is written down where there is no renderer left to explain it */}
      {!card.takeable ? (
        <p className="wx-note">{t('wx.nearby.own', { id: card.id })}</p>
      ) : card.atisRow ? (
        <p className="wx-note">{t('wx.nearby.taken', { row: card.atisRow, id: card.id, of: card.of })}</p>
      ) : null}
    </div>
  );
}
